<template>
    <div>
        <Loader :visible="isLoading" :text="loadingText" />

        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
        </div>

        <div class="p-6">
            <div class="mb-6">
                <form @submit.prevent="doSearch" class="flex flex-col gap-2 sm:flex-row sm:gap-3">
                    <input
                        v-model="searchQuery"
                        type="text"
                        class="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-700 shadow-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-100"
                        placeholder="Cerca per codice o descrizione..."
                        autocomplete="off"
                    />
                    <div class="flex gap-2 sm:gap-3">
                        <button
                            type="submit"
                            :disabled="isLoading || searchQuery.trim().length < 2"
                            class="flex-1 rounded-lg bg-green-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-green-700 disabled:opacity-50 sm:flex-none"
                        >
                            Cerca
                        </button>
                        <button
                            v-if="items.length || searchPerformed"
                            type="button"
                            @click="resetAll"
                            class="flex-1 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 sm:flex-none"
                        >
                            Reset
                        </button>
                    </div>
                </form>
            </div>

            <div v-if="searchPerformed && items.length === 0 && !isLoading" class="rounded-lg border border-gray-200 bg-white px-6 py-10 text-center text-sm text-gray-500 shadow-sm">
                Nessun articolo trovato.
            </div>

            <div v-if="items.length > 0">
                <!-- Desktop table -->
                <div class="hidden sm:block overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                    <table class="min-w-full divide-y divide-gray-200 text-sm">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="px-4 py-3 text-left font-semibold text-gray-600">Codice</th>
                                <th class="px-4 py-3 text-left font-semibold text-gray-600">Descrizione</th>
                                <th class="px-4 py-3 text-left font-semibold text-gray-600">Barcode attuale</th>
                                <th class="px-4 py-3 text-center font-semibold text-gray-600">Azione</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-100">
                            <tr
                                v-for="item in items"
                                :key="item.Item"
                                :class="selectedItem && selectedItem.Item === item.Item ? 'bg-green-50' : 'hover:bg-gray-50'"
                            >
                                <td class="px-4 py-3 font-mono text-gray-800">{{ item.Item }}</td>
                                <td class="px-4 py-3 text-gray-700">{{ item.Description }}</td>
                                <td class="px-4 py-3">
                                    <span v-if="item.SaleBarCode" class="font-mono text-gray-800">{{ item.SaleBarCode }}</span>
                                    <span v-else class="italic text-gray-400">—</span>
                                </td>
                                <td class="px-4 py-3 text-center">
                                    <button
                                        @click="selectItem(item)"
                                        class="rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-green-700"
                                    >
                                        Modifica
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div v-if="total > items.length" class="flex items-center justify-between border-t border-gray-200 px-4 py-3">
                        <span class="text-sm text-gray-500">
                            Mostrati {{ items.length }} di {{ total }} risultati
                        </span>
                        <button
                            @click="loadMore"
                            :disabled="isLoading"
                            class="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-50"
                        >
                            Carica altri
                        </button>
                    </div>
                </div>

                <!-- Mobile cards -->
                <div class="sm:hidden space-y-3">
                    <div
                        v-for="item in items"
                        :key="item.Item"
                        :class="[
                            'rounded-xl border bg-white shadow-sm p-4',
                            selectedItem && selectedItem.Item === item.Item ? 'border-green-400 bg-green-50' : 'border-gray-200'
                        ]"
                    >
                        <div class="flex items-start justify-between gap-3">
                            <div class="min-w-0 flex-1">
                                <p class="font-mono text-sm font-semibold text-gray-800">{{ item.Item }}</p>
                                <p class="mt-0.5 text-sm text-gray-600 break-words">{{ item.Description }}</p>
                                <p class="mt-2 text-xs text-gray-500">
                                    Barcode:
                                    <span v-if="item.SaleBarCode" class="font-mono text-gray-800">{{ item.SaleBarCode }}</span>
                                    <span v-else class="italic text-gray-400">—</span>
                                </p>
                            </div>
                            <button
                                @click="selectItem(item)"
                                class="shrink-0 rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-green-700"
                            >
                                Modifica
                            </button>
                        </div>
                    </div>

                    <div v-if="total > items.length" class="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                        <span class="text-sm text-gray-500">
                            Mostrati {{ items.length }} di {{ total }}
                        </span>
                        <button
                            @click="loadMore"
                            :disabled="isLoading"
                            class="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-50"
                        >
                            Carica altri
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div
            v-if="selectedItem"
            class="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
           
        >
            <div class="w-full rounded-t-xl bg-white shadow-2xl sm:max-w-md sm:rounded-xl">
                <div class="border-b border-gray-200 px-6 py-4">
                    <h2 class="text-base font-semibold text-gray-900">Associa Barcode</h2>
                    <p class="mt-0.5 text-sm text-gray-500">
                        <span class="font-mono font-medium text-gray-800">{{ selectedItem.Item }}</span>
                        &mdash; {{ selectedItem.Description }}
                    </p>
                </div>

                <form @submit.prevent="saveBarcode" class="px-6 py-5">
                    <label class="mb-1.5 block text-sm font-medium text-gray-700">
                        Barcode (SaleBarCode)
                    </label>
                    <input
                        v-model="barcodeInput"
                        ref="barcodeInputRef"
                        type="text"
                        class="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-mono text-gray-800 shadow-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-100"
                        placeholder="Inserisci barcode..."
                        autocomplete="off"
                    />
                    <p v-if="formError" class="mt-2 text-sm text-red-600">{{ formError }}</p>

                    <div class="mt-5 flex justify-end gap-3">
                        <button
                            type="button"
                            @click="closeModal"
                            class="rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
                        >
                            Annulla
                        </button>
                        <button
                            type="submit"
                            :disabled="isSaving"
                            class="rounded-lg bg-green-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-green-700 disabled:opacity-50"
                        >
                            {{ isSaving ? 'Salvataggio...' : 'Salva' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <div
            v-if="toast.show"
            :class="[
                'fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-lg px-5 py-3 text-sm font-medium text-white shadow-lg transition-all',
                toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
            ]"
        >
            {{ toast.message }}
        </div>
    </div>
</template>

<script>
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import Loader from '@/Components/Loader.vue';

export default {
    components: {
        BreadCrumbs,
        Loader,
    },

    data() {
        return {
            navigation: [
                { name: 'Dashboard', href: route('dashboard') },
                { name: this.$t('modules.gestione-magazzino'), href: route('modules.home', 'gestione-magazzino') },
                { name: this.$t('modules.associa-barcode'), href: '#' },
            ],

            searchQuery: '',
            searchPerformed: false,
            currentPage: 1,
            items: [],
            total: 0,

            selectedItem: null,
            barcodeInput: '',
            formError: '',
            isSaving: false,

            isLoading: false,
            loadingText: '',

            toast: {
                show: false,
                message: '',
                type: 'success',
            },
        };
    },

    methods: {
        async doSearch() {
            if (this.searchQuery.trim().length < 2) return;

            this.currentPage = 1;
            this.items = [];
            this.total = 0;
            this.searchPerformed = false;
            this.isLoading = true;
            this.loadingText = 'Ricerca in corso...';

            try {
                const res = await axios.post(route('modules.gm.associabarcode.search'), {
                    search: this.searchQuery.trim(),
                    page: 1,
                });
                this.items = res.data.items ?? [];
                this.total = res.data.total ?? 0;
                this.searchPerformed = true;
            } catch {
                this.showToast('Errore durante la ricerca.', 'error');
            } finally {
                this.isLoading = false;
                this.loadingText = '';
            }
        },

        async loadMore() {
            this.currentPage += 1;
            this.isLoading = true;
            this.loadingText = 'Caricamento...';

            try {
                const res = await axios.post(route('modules.gm.associabarcode.search'), {
                    search: this.searchQuery.trim(),
                    page: this.currentPage,
                });
                this.items.push(...(res.data.items ?? []));
                this.total = res.data.total ?? this.total;
            } catch {
                this.showToast('Errore durante il caricamento.', 'error');
            } finally {
                this.isLoading = false;
                this.loadingText = '';
            }
        },

        selectItem(item) {
            this.selectedItem = { ...item };
            this.barcodeInput = item.SaleBarCode || '';
            this.formError = '';
            this.$nextTick(() => {
                this.$refs.barcodeInputRef?.focus();
            });
        },

        closeModal() {
            this.selectedItem = null;
            this.barcodeInput = '';
            this.formError = '';
        },

        async saveBarcode() {
            this.formError = '';
            this.isSaving = true;

            try {
                const res = await axios.post(route('modules.gm.associabarcode.update'), {
                    item: this.selectedItem.Item,
                    barcode: this.barcodeInput.trim(),
                });

                if (res.data.success) {
                    const idx = this.items.findIndex(i => i.Item === this.selectedItem.Item);
                    if (idx !== -1) {
                        this.items[idx].SaleBarCode = this.barcodeInput.trim();
                    }
                    this.showToast('Barcode aggiornato con successo.', 'success');
                    this.closeModal();
                } else {
                    this.formError = res.data.message || 'Errore durante il salvataggio.';
                }
            } catch {
                this.formError = 'Errore di rete durante il salvataggio.';
            } finally {
                this.isSaving = false;
            }
        },

        resetAll() {
            this.searchQuery = '';
            this.items = [];
            this.total = 0;
            this.currentPage = 1;
            this.searchPerformed = false;
            this.closeModal();
        },

        showToast(message, type = 'success') {
            this.toast.message = message;
            this.toast.type = type;
            this.toast.show = true;
            setTimeout(() => {
                this.toast.show = false;
            }, 3500);
        },
    },
};
</script>
